import { Grid, Typography } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import ReactHookFormSelect from '../ReactHookForm/ReactHookFormSelect';
import ReactHookFormTextField from '../ReactHookForm/ReactHookFormTextField';
import { fieldIsRequiredMsg } from '../../utils/componentUtils';
import FormDialog from '../Dialogs/FormDialog';
import ReactHookFormCheckbox from '../ReactHookForm/ReactHookFormCheckbox';

const WorkingUnitDialog = ({
    workingUnit,
    showDialog,
    channelOptions,
    taskOptions,
    worktypeOptions,
    onSubmit,
    isLoading,
    onClose,
}) => {
    const form = useForm();
    const {
        formState: { errors },
        control,
        reset,
    } = form;

    useEffect(() => {
        reset({
            id: workingUnit?.id || '',
            worktype: workingUnit?.worktypeId || '',
            channel: workingUnit?.channelId || '',
            task: workingUnit?.taskId || '',
            shortName: workingUnit?.shortName || '',
            matchWithOfferedCluster: workingUnit?.id ? !!workingUnit?.offeredClusterId : true,
        });
    }, [workingUnit, reset]);

    // Watch form values to compute preview
    const watchedWorktype = useWatch({ control, name: 'worktype' });
    const watchedChannel = useWatch({ control, name: 'channel' });
    const watchedTask = useWatch({ control, name: 'task' });
    // Compose the shortName preview from component shortNames
    const composedShortName = useMemo(() => {
        const selectedWorktype = worktypeOptions?.find((w) => w.id === watchedWorktype);
        const selectedChannel = channelOptions?.find((c) => c.id === watchedChannel);
        const selectedTask = taskOptions?.find((t) => t.id === watchedTask);

        const parts = [
            selectedWorktype?.shortName || selectedWorktype?.worktype,
            selectedChannel?.shortName || selectedChannel?.name,
            selectedTask?.shortName || selectedTask?.name,
        ].filter(Boolean);

        return parts.join(' - ');
    }, [watchedWorktype, watchedChannel, watchedTask, worktypeOptions, channelOptions, taskOptions]);

    const handleClose = () => {
        onClose();
    };

    return (
        <FormDialog
            title={workingUnit?.id ? 'Bearbeiten der Arbeitseinheit' : 'Neue Arbeitseinheit erstellen'}
            showDialog={showDialog}
            form={form}
            onSubmit={onSubmit}
            onClose={handleClose}
            isLoading={isLoading}
        >
            <Grid item xs={12}>
                <ReactHookFormSelect
                    name="worktype"
                    options={worktypeOptions}
                    mainProp="worktype"
                    label="Worktype"
                    control={control}
                    rules={{ required: { value: true, message: fieldIsRequiredMsg } }}
                    formErrors={errors}
                ></ReactHookFormSelect>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormSelect
                    name="channel"
                    options={channelOptions}
                    mainProp="name"
                    label="Channel"
                    control={control}
                    rules={{ required: { value: true, message: fieldIsRequiredMsg } }}
                    formErrors={errors}
                ></ReactHookFormSelect>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormSelect
                    name="task"
                    options={taskOptions}
                    mainProp="name"
                    label="Task"
                    control={control}
                    rules={{ required: { value: true, message: fieldIsRequiredMsg } }}
                    formErrors={errors}
                ></ReactHookFormSelect>
            </Grid>
            <Grid item xs={12}>
                <Typography
                    variant="body2"
                    sx={{ mb: 1 }}
                    color={composedShortName.length > 31 ? 'error' : 'textPrimary'}
                >
                    <span>
                        Composed Short Name: <strong>{composedShortName}</strong> ({composedShortName.length}/31)
                    </span>
                </Typography>
                <ReactHookFormTextField
                    name="shortName"
                    label="Short Name Override (not recommended)"
                    control={control}
                    rules={{ maxLength: { value: 31, message: 'Max 31 characters' } }}
                    formErrors={errors}
                    isNumeric={false}
                    defaultValue=""
                    inputProps={{ maxLength: 31 }}
                    isMultiline={false}
                ></ReactHookFormTextField>
                <Typography variant="caption" color="textSecondary">
                    <span>Prefer editing short names on Worktype/Channel/Task pages.</span>
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormCheckbox
                    name="matchWithOfferedCluster"
                    control={control}
                    label="Match with Offered Cluster"
                    formErrors={errors}
                ></ReactHookFormCheckbox>
            </Grid>
        </FormDialog>
    );
};

export default WorkingUnitDialog;
